import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import DOMPurify from 'dompurify'
import { Box, Button } from '@material-ui/core'
import PropTypes from 'prop-types'

import { DocumentContent } from '../'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import schema from './schema'
import { SETTINGS_FIELDS } from './settingsSchema'
import { EDITOR_FIELDS } from './editorSchema'
import * as service from 'service'
import constants from 'constants/index'

const {
  DOCUMENTS_CATEGORY_ID,
  SETTINGS_DOCUMENTS_CATEGORY_ID,
  WRITTEN_CONTENT_KIND,
  ATTACHMENT_CONTENT_KIND,
  EXPIRED_STATUS_ID,
} = constants.documents

const buildDefaultValues = (doc, permissions, expirationConfigs) => {
  let initialSelectedUsers = []
  let initialSelectedDepartments = []

  if (permissions) {
    initialSelectedUsers = permissions
      .filter((p) => p.targetType === constants.documents.TARGET_TYPE_USER)
      .map((p) => p.target)

    initialSelectedDepartments = permissions
      .filter(
        (p) => p.targetType === constants.documents.TARGET_TYPE_DEPARTMENT,
      )
      .map((p) => p.target)
  }

  return {
    allowAutomaticEditing: doc?.allowAutomaticEditing || false,
    attachment: doc?.attachment || null,
    category: doc?.category || '',
    checkOldVersions: doc?.checkOldVersions || false,
    code: doc?.code || '',
    collectAccepted: doc?.collectAccepted || false,
    collectApproval: doc?.collectApproval || false,
    collectSignedDocument: doc?.collectSignedDocument || false,
    signedDocumentLink: doc?.signedDocument?.link || '',
    content: DOMPurify.sanitize(doc?.content || ''),
    contentKind: doc?.contentKind || WRITTEN_CONTENT_KIND,
    otherCategory: doc?.otherCategory || '',
    publicDocument: doc?.publicDocument || false,
    selectedDepartments: initialSelectedDepartments,
    selectedUsers: initialSelectedUsers,
    signedDocument: doc?.signedDocument?.fileInfo || null,
    title: doc?.title || '',
    visibilityType: doc?.visibilityType || '',
    dueDate: expirationConfigs?.active || false,
    expirationType:
      expirationConfigs?.expirationType === 'signature' ? '1' : '0',
    expirationUnit: expirationConfigs?.expirationUnit || null,
    expirationTime: expirationConfigs?.expirationTime || null,
    dueDateValue: expirationConfigs?.dueDate
      ? new Date(expirationConfigs.dueDate)
      : null,
  }
}

const DocumentEditForm = ({
  availableUsers,
  consentForm,
  currentDocument,
  currentTab,
  departments,
  departmentsIsLoading,
  documentId,
  handleBackRedirection,
  isLoadingResponses,
  permissionsIsLoading,
  privacyPermissionsResponse,
  refresh,
  setCurrentTab,
  setLoading,
  usersIsLoading,
  expirationConfigs,
  controls = {},
  versions = {},
}) => {
  const snackbar = useSnackbar()

  const [openVersionDialog, setOpenVersionDialog] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(null)
  const [formInitialized, setFormInitialized] = useState(false)

  const settingsErrorRef = useRef(null)

  const isActive =
    currentDocument?.status === constants.documents.ACTIVE_STATUS_ID
  const isAwaitingApproval =
    currentDocument?.status === constants.documents.AWAITING_APPROVAL_STATUS_ID
  const collectAccepted = currentDocument?.collectAccepted || false

  const isWrittenContent = currentDocument?.contentKind === WRITTEN_CONTENT_KIND

  const formMethods = useForm({
    validationSchema: schema(isWrittenContent),
    defaultValues: buildDefaultValues(null, null, expirationConfigs),
  })

  const { handleSubmit, getValues, triggerValidation, reset, watch, setValue } =
    formMethods

  const updateType = watch('updateType')

  const isAttachmentContent =
    currentDocument?.contentKind === ATTACHMENT_CONTENT_KIND

  useEffect(() => {
    if (
      !formInitialized &&
      currentDocument &&
      !isLoadingResponses &&
      !permissionsIsLoading
    ) {
      const permissionsList =
        privacyPermissionsResponse?.data?.privacyPermissions
      reset(
        buildDefaultValues(currentDocument, permissionsList, expirationConfigs),
      )
      setFormInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDocument,
    isLoadingResponses,
    privacyPermissionsResponse,
    permissionsIsLoading,
  ])

  useEffect(() => {
    if (isAttachmentContent) {
      setValue('publicDocument', false)
      setValue('collectAccepted', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAttachmentContent])

  const { getChangedValues } = helpers.form

  const handleSubmitValidation = async (status) => {
    const isValid = await triggerValidation()

    if (!isValid) {
      const hasSettingsError = SETTINGS_FIELDS.some(
        (field) => !!formMethods.errors[field],
      )
      const hasEditorError = EDITOR_FIELDS.some(
        (field) => !!formMethods.errors[field],
      )
      if (hasSettingsError && !hasEditorError) {
        setCurrentTab(SETTINGS_DOCUMENTS_CATEGORY_ID)
        setTimeout(
          () =>
            settingsErrorRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            }),
          100,
        )
      }
      return
    }

    const formValues = getValues()
    const currentDefaultValues = buildDefaultValues(
      currentDocument,
      privacyPermissionsResponse?.data?.privacyPermissions,
      expirationConfigs,
    )

    const changedRelevantValues = getChangedValues(
      { content: formValues.content },
      { content: currentDefaultValues.content },
    )

    const changedAnyValues = getChangedValues(formValues, currentDefaultValues)
    const changeStatus = status !== currentDocument?.status

    if (isEmpty(changedAnyValues) && !changeStatus) return

    if (
      !isEmpty(changedRelevantValues) &&
      (isActive || isAwaitingApproval) &&
      !isAttachmentContent
    ) {
      setPendingStatus(status)
      setOpenVersionDialog(true)
    } else {
      handleSubmit((data) => onSubmit(data, status))()
    }
  }

  const onSubmit = async (data, status) => {
    const resolvedStatus = status ?? pendingStatus
    const formValues = getValues()
    const currentDefaultValues = buildDefaultValues(
      currentDocument,
      privacyPermissionsResponse?.data?.privacyPermissions,
      expirationConfigs,
    )
    if (collectAccepted) {
      data.expirationType = currentDefaultValues.expirationType
      data.expirationUnit = currentDefaultValues.expirationUnit
      data.expirationTime = currentDefaultValues.expirationTime
      data.dueDateValue = currentDefaultValues.dueDateValue
    }

    const changedPermissions = getChangedValues(
      {
        publicDocument: data.publicDocument,
        collectAccepted: data.collectAccepted,
        allowAutomaticEditing: data.allowAutomaticEditing,
        checkOldVersions: data.checkOldVersions,
        collectApproval: data.collectApproval,
        collectSignedDocument: data.collectSignedDocument,
      },
      {
        publicDocument: currentDefaultValues.publicDocument,
        collectAccepted: currentDefaultValues.collectAccepted,
        allowAutomaticEditing: currentDefaultValues.allowAutomaticEditing,
        checkOldVersions: currentDefaultValues.checkOldVersions,
        collectApproval: currentDefaultValues.collectApproval,
        collectSignedDocument: currentDefaultValues.collectSignedDocument,
      },
    )

    const changedMetadata = getChangedValues(
      {
        title: data.title,
        code: data.code,
        category: data.category,
        otherCategory: data.otherCategory,
        visibilityType: data.visibilityType,
        selectedUsers: data.selectedUsers,
        selectedDepartments: data.selectedDepartments,
      },
      {
        title: currentDefaultValues.title,
        code: currentDefaultValues.code,
        category: currentDefaultValues.category,
        otherCategory: currentDefaultValues.otherCategory,
        visibilityType: currentDefaultValues.visibilityType,
        selectedUsers: currentDefaultValues.selectedUsers,
        selectedDepartments: currentDefaultValues.selectedDepartments,
      },
    )

    const changedExpiration = getChangedValues(
      {
        dueDate: data.dueDate,
        expirationType: data.expirationType,
        expirationUnit: data.expirationUnit,
        expirationTime: data.expirationTime,
        dueDateValue: data.dueDateValue,
      },
      {
        dueDate: currentDefaultValues.dueDate,
        expirationType: currentDefaultValues.expirationType,
        expirationUnit: currentDefaultValues.expirationUnit,
        expirationTime: currentDefaultValues.expirationTime,
        dueDateValue: currentDefaultValues.dueDateValue,
      },
    )

    let finalUpdateType = updateType
    const isExpired = currentDocument?.status === EXPIRED_STATUS_ID

    if (
      (!isEmpty(changedPermissions) || !isEmpty(changedMetadata)) &&
      !updateType
    ) {
      finalUpdateType = 'pass'
    }

    const changedContent = getChangedValues(
      { content: data.content },
      { content: currentDefaultValues.content },
    )

    const onlyExpirationChanged =
      !isEmpty(changedExpiration) &&
      isEmpty(changedPermissions) &&
      isEmpty(changedMetadata) &&
      isEmpty(changedContent)

    if (onlyExpirationChanged) {
      finalUpdateType = 'pass'
    }

    if (!updateType && !finalUpdateType && !isEmpty(changedContent)) {
      snackbar.open({
        message: 'Selecione uma opção para o tipo de atualização.',
        variant: 'error',
      })
      return
    }

    try {
      setLoading(true)

      const changedValues = helpers.form.getChangedValues(
        data,
        currentDefaultValues,
      )

      const expirationData = {
        active: data?.dueDate || false,
        expirationType: data?.expirationType || null,
        expirationUnit: data?.expirationUnit || null,
        expirationTime:
          data?.expirationTime !== undefined && data?.expirationTime !== ''
            ? data.expirationTime
            : null,
        dueDate: helpers.formatters.date(data?.dueDateValue) || null,
      }

      const {
        selectedUsers,
        selectedDepartments,
        visibilityType,
        dueDateValue,
        dueDate,
        expirationType,
        expirationUnit,
        expirationTime,
        ...documentData
      } = changedValues

      const currentVisibilityType = formValues.visibilityType

      let targetIds = []
      if (
        currentVisibilityType === constants.documents.USER_ACCESS_PERMISSION_ID
      ) {
        targetIds = formValues.selectedUsers?.map((u) => u.id) || []
      } else if (
        currentVisibilityType ===
        constants.documents.DEPARTMENT_ACCESS_PERMISSION_ID
      ) {
        targetIds = formValues.selectedDepartments?.map((d) => d.id) || []
      }

      data.dueDateValue = helpers.formatters.date(data.dueDateValue)

      await service.dponet.privacyPolicies.put({
        privacyPolicyId: currentDocument?.id,
        updateExpiration: true,
        status: resolvedStatus,
        collectAccepted,
        updateType: updateType || finalUpdateType,
        isExpiredReactivation: isExpired,
        ...documentData,
        ...expirationData,
        ...(data.signedDocument instanceof File
          ? { signedDocument: data.signedDocument }
          : {}),
        ...(data.signedDocument === null && currentDefaultValues.signedDocument
          ? { removeSignedDocument: true }
          : {}),
      })

      if (
        visibilityType !== undefined ||
        selectedUsers !== undefined ||
        selectedDepartments !== undefined
      ) {
        await service.dponet.privacyPolicies.updatePrivacyPermissions({
          privacyPolicyId: currentDocument?.id,
          visibilityType: currentVisibilityType,
          targetIds,
        })
      }

      snackbar.open({
        message: 'Documento editado com sucesso!',
        variant: 'success',
      })

      setOpenVersionDialog(false)
      setPendingStatus(null)
      refresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu uma falha ao tentar editar o documento!',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const formActionButtons = (
    <Box display="flex" gridGap={theme.spacing(1)} justifyContent="flex-end">
      <Button
        variant="outlined"
        color="primary"
        onClick={
          currentTab === DOCUMENTS_CATEGORY_ID
            ? handleBackRedirection
            : () => setCurrentTab(DOCUMENTS_CATEGORY_ID)
        }
      >
        Voltar
      </Button>
      <Button
        variant="outlined"
        color="primary"
        onClick={() =>
          handleSubmitValidation(constants.documents.DRAFT_STATUS_ID)
        }
      >
        Salvar Rascunho
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() =>
          handleSubmitValidation(constants.documents.ACTIVE_STATUS_ID)
        }
      >
        {isActive ? 'Salvar' : 'Publicar'}
      </Button>
    </Box>
  )

  return (
    <DocumentContent
      type="edit"
      currentTab={currentTab}
      isLoading={isLoadingResponses}
      documentData={{
        id: documentId,
        current: currentDocument,
        consentForm,
        isWrittenContent,
      }}
      form={{
        methods: formMethods,
        actionButtons: formActionButtons,
        onSubmit,
        errorRef: settingsErrorRef,
        initialized: formInitialized,
      }}
      pageActions={{ handleBackRedirection, setLoading, refresh }}
      versionAlertDialog={{
        open: openVersionDialog,
        setOpen: setOpenVersionDialog,
      }}
      settings={{
        availableUsers,
        departments,
        permissionsIsLoading,
        usersIsLoading,
        departmentsIsLoading,
      }}
      versions={versions}
      controls={controls}
    />
  )
}

DocumentEditForm.propTypes = {
  availableUsers: PropTypes.array,
  consentForm: PropTypes.object,
  currentDocument: PropTypes.object,
  currentTab: PropTypes.string.isRequired,
  departments: PropTypes.array,
  departmentsIsLoading: PropTypes.bool,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleBackRedirection: PropTypes.func,
  isLoadingResponses: PropTypes.bool,
  onContentKindChange: PropTypes.func,
  permissionsIsLoading: PropTypes.bool,
  privacyPermissionsResponse: PropTypes.object,
  refresh: PropTypes.func,
  setCurrentTab: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  usersIsLoading: PropTypes.bool,
  expirationConfigs: PropTypes.object,
  controls: PropTypes.shape({
    linkedDocuments: PropTypes.array,
    isLoading: PropTypes.bool,
    pagination: PropTypes.node,
    refresh: PropTypes.func,
  }),
  versions: PropTypes.shape({
    documentVersions: PropTypes.array,
    isLoading: PropTypes.bool,
    downloadDocument: PropTypes.func,
    pagination: PropTypes.node,
  }),
}

export default DocumentEditForm
